import * as React from "react";
import { Link as RouterLink, useHistory } from "react-router-dom";
import { HashLink } from "react-router-hash-link";

import Avatar from "@material-ui/core/Avatar";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Card from "@material-ui/core/Card";
import CardActions from "@material-ui/core/CardActions";
import CardContent from "@material-ui/core/CardContent";
import Divider from "@material-ui/core/Divider";
import Grid from "@material-ui/core/Grid";
import Link from "@material-ui/core/Link";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import Typography from "@material-ui/core/Typography";
import useMediaQuery from "@material-ui/core/useMediaQuery";

import styled from "@material-ui/styles/styled";
import useTheme from "@material-ui/core/styles/useTheme";
import { darken } from "@material-ui/core/styles/colorManipulator";
import orange from "@material-ui/core/colors/orange";
import lightBlue from "@material-ui/core/colors/lightBlue";

import Assistant from "@material-ui/icons/Assistant";
import Brush from "@material-ui/icons/Brush";
import CreditCard from "@material-ui/icons/CreditCard";
import Edit from "@material-ui/icons/Edit";
import Equalizer from "@material-ui/icons/Equalizer";
import MenuBook from "@material-ui/icons/MenuBook";
import Speed from "@material-ui/icons/Speed";
import Timer from "@material-ui/icons/Timer";

import { PageWrapper } from "../components/PageWrapper/PageWrapper";
import { Container } from "../components/PageWrapper/Container";
import { ColoredBandContainer } from "src/components/PageWrapper/ColoredBandContainer";
import { INFO_EMAIL, SAMPLE_REPORT_URL } from "../utils/Constants";
import { Testimonials } from "../components/Testimonials/Testimonials";
import { Iris, IrisAssessment, IrisEditor } from "../components/Iris";
import { PageHeader } from "../components/PageWrapper/PageHeader";
import { SignupButton } from "../components/SignupButton/SignupButton";
import { BlogListEntry } from "../components/Blog/BlogListEntry";
import {
  EDITOR_LAUNCH_BLOG,
  COMPETITION_WINNER_BLOG,
} from "../components/Blog/Blog";

import primary_hero from "../images/primary.jpeg";
import publisher from "../images/publisher.svg";
import author from "../images/author.svg";
import agent from "../images/agent.svg";

const HeroBox = styled(Box)({
  backgroundColor: "rgba(75, 159, 234, 0.1)", // primary alpha 0.2
  position: "relative",
  "&::after": {
    position: "absolute",
    content: '""',
    width: "30%",
    zIndex: 1,
    top: 0,
    left: "5%",
    height: "100%",
    backgroundSize: "18px 18px",
    backgroundImage: `radial-gradient(rgba(75, 159, 234, 0.5) 20%, transparent 20%)`,
    opacity: 0.2,
  },
});

const BenefitImage = styled("img")({
  width: "100%",
  height: 120,
});

const BenefitImageSmaller = styled("img")({
  width: "100%",
  height: 90,
  marginBottom: 30,
});

export const MarketingPage = () => {
  return (
    <PageWrapper childrenInContainer={false}>
      <Hero />
      <Container>
        <Report />
      </Container>
      <ColoredBandContainer>
        <ProductOverview />
      </ColoredBandContainer>
      <Container>
        <SpecialsAndNews />
      </Container>
      <GetStarted />
      <Container>
        <Benefits />
        <Box my={8}>
          <Testimonials showHeader={true} />
        </Box>
      </Container>
    </PageWrapper>
  );
};

const Hero = () => {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up("md"), {
    defaultMatches: true,
  });

  return (
    <HeroBox>
      <Box position="relative" zIndex={3}>
        <Box
          style={{
            overflow: "hidden",
          }}
        >
          <Box maxWidth={{ md: 1236 }} margin="0 auto" px={0} py={0}>
            <Box
              display="flex"
              flexDirection={isMd ? "row" : "column"}
              position="relative"
              minHeight={isMd ? 563 : 0}
            >
              <Box
                order={isMd ? 1 : 2}
                display="flex"
                alignItems="center"
                mx={isMd ? 0 : "auto"}
                my={isMd ? 0 : 4}
                maxWidth={isMd ? undefined : 720}
              >
                <Box mx={2}>
                  <Box marginBottom={2}>
                    <Typography
                      variant="h2"
                      color="textPrimary"
                      variantMapping={{ h2: "h1" }}
                      style={{ fontWeight: 700 }}
                    >
                      Sell better stories{" "}
                    </Typography>
                    <Typography
                      component="span"
                      variant="h2"
                      color="primary"
                      variantMapping={{ h2: "h1" }}
                      style={{
                        background: `linear-gradient(180deg, transparent 82%, ${orange[100]} 0%)`,
                      }}
                    >
                      with science
                    </Typography>
                  </Box>
                  <Box marginBottom={3}>
                    <Typography
                      variant="h6"
                      component="p"
                      color="textSecondary"
                    >
                      Real magic happens when human creativity is complemented
                      by the power of today's cutting edge technology.
                    </Typography>
                  </Box>
                  <Box>
                    <SignupButton
                      place="home-page"
                      label="Sign up now"
                      textAlign="left"
                    />
                  </Box>
                </Box>
              </Box>
              <Box
                flex={isMd ? "0 0 50%" : "0 0 100%"}
                position="relative"
                maxWidth={isMd ? "50%" : "100%"}
                order={isMd ? 2 : 1}
              >
                <Box
                  width={isMd ? "50vw" : 1}
                  height="100%"
                  position="relative"
                >
                  <Box width="100%" height="100%" overflow="hidden">
                    <Box
                      overflow="hidden"
                      left="0%"
                      width={1}
                      height={1}
                      position={isMd ? "absolute" : "relative"}
                      style={{
                        clipPath: isMd
                          ? "polygon(10% 0%, 100% 0, 100% 100%, 0% 100%)"
                          : "none",
                        shapeOutside: isMd
                          ? "polygon(10% 0%, 100% 0, 100% 100%, 0% 100%)"
                          : "none",
                      }}
                    >
                      <Box
                        height={isMd ? 1 : "auto"}
                        display={isMd ? "initial" : "none"}
                      >
                        <Box
                          height={{ xs: "auto", md: 1 }}
                          maxHeight={{ xs: 300, md: 1 }}
                          width={1}
                          maxWidth={1}
                        >
                          <img
                            src={primary_hero}
                            loading="lazy"
                            alt="Person with books"
                            style={{ objectFit: "cover" }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      <Divider />
    </HeroBox>
  );
};

const ProductOverview = () => {
  const theme = useTheme();

  return (
    <Box>
      <PageHeader align="center" overline="Products" />
      <Grid container={true} spacing={4}>
        <Grid item={true} xs={12} md={6}>
          <Card
            elevation={4}
            style={{
              height: "100%",
              display: "flex",
            }}
          >
            <CardContent style={{ display: "flex", flexDirection: "column" }}>
              <Box mb={2} flexGrow={0} flexShrink={0} display="flex">
                <Box flexGrow={1} flexShrink={1}>
                  <Typography
                    variant="h5"
                    color="textPrimary"
                    gutterBottom={true}
                  >
                    <IrisEditor />
                  </Typography>
                </Box>
                <Box flexGrow={0} flexShrink={0}>
                  <Avatar
                    variant="circle"
                    style={{
                      backgroundColor: theme.palette.primary.main,
                    }}
                  >
                    <Edit />
                  </Avatar>
                </Box>
              </Box>
              <Box flexGrow={1} flexShrink={1}>
                <Typography
                  variant="body1"
                  color="textSecondary"
                  gutterBottom={true}
                >
                  <IrisEditor /> is your intelligent writing assistant,
                  providing assessments and editorial hints. It's like Grammarly
                  for fiction authors!
                </Typography>
                <Typography
                  variant="body1"
                  color="primary"
                  gutterBottom={true}
                  style={{ marginTop: 12 }}
                >
                  <b>
                    Whether you're drafting your first novel or polishing your
                    next bestseller, Iris delivers real-time feedback tailored
                    to creative storytelling.
                  </b>
                </Typography>
              </Box>
              <Box flexGrow={0} flexShrink={0}>
                <List dense={true}>
                  <ListItem disableGutters={true}>
                    <ListItemIcon>
                      <Speed color="secondary" />
                    </ListItemIcon>
                    <ListItemText primary="Predictions for sellability, rating and genre fit" />
                  </ListItem>
                  <ListItem disableGutters={true}>
                    <ListItemIcon>
                      <Brush color="secondary" />
                    </ListItemIcon>
                    <ListItemText primary="30+ style metrics with charts and actionable feedback" />
                  </ListItem>
                  <ListItem disableGutters={true}>
                    <ListItemIcon>
                      <Equalizer color="secondary" />
                    </ListItemIcon>
                    <ListItemText primary="Bestseller and genre comparisons" />
                  </ListItem>
                  <ListItem disableGutters={true}>
                    <ListItemIcon>
                      <Timer color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Feedback in real-time as you type" />
                  </ListItem>
                  <ListItem disableGutters={true}>
                    <ListItemIcon>
                      <Assistant color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Deep content-related feedback and editing suggestions" />
                  </ListItem>
                </List>
              </Box>
              <Box
                flexGrow={0}
                flexShrink={0}
                mt={2}
                display="flex"
                justifyContent="space-between"
              >
                <Box flexGrow={0} flexShrink={0}>
                  <Button
                    component={RouterLink}
                    to="/editor"
                    variant="text"
                    color="primary"
                  >
                    Learn More
                  </Button>
                </Box>
                <Box flexGrow={0} flexShrink={0}>
                  <SignupButton
                    place="home-page-editor"
                    label="Sign up now and get 7 days free"
                    size="medium"
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item={true} xs={12} md={6}>
          <Card
            elevation={4}
            style={{
              height: "100%",
              display: "flex",
            }}
          >
            <CardContent style={{ display: "flex", flexDirection: "column" }}>
              <Box mb={2} flexGrow={0} flexShrink={0} display="flex">
                <Box flexGrow={1} flexShrink={1}>
                  <Typography
                    variant="h5"
                    color="textPrimary"
                    gutterBottom={true}
                  >
                    <IrisAssessment />
                  </Typography>
                </Box>
                <Box flexGrow={0} flexShrink={0}>
                  <Avatar
                    variant="circle"
                    style={{
                      backgroundColor: theme.palette.primary.main,
                    }}
                  >
                    <MenuBook />
                  </Avatar>
                </Box>
              </Box>
              <Box flexGrow={1} flexShrink={1}>
                <Typography
                  variant="body1"
                  color="textSecondary"
                  gutterBottom={true}
                >
                  <IrisAssessment /> reates a full, detailed report you can
                  share with publishers, agents or editors.
                </Typography>
                <Typography
                  variant="body1"
                  color="primary"
                  gutterBottom={true}
                  style={{ marginTop: 12 }}
                >
                  <b>
                    Upload your manuscript and get actionable feedback for your
                    entire work across 30+ metrics within minutes, is ~30x more
                    affordable than traditional manuscript assessments.
                  </b>
                </Typography>
              </Box>
              <Box flexGrow={0} flexShrink={0}>
                <List dense={true}>
                  <ListItem disableGutters={true}>
                    <ListItemIcon>
                      <Speed color="secondary" />
                    </ListItemIcon>
                    <ListItemText primary="Predictions for sellability, rating and genre fit" />
                  </ListItem>
                  <ListItem disableGutters={true}>
                    <ListItemIcon>
                      <Brush color="secondary" />
                    </ListItemIcon>
                    <ListItemText primary="30+ style metrics with charts and actionable feedback" />
                  </ListItem>
                  <ListItem disableGutters={true}>
                    <ListItemIcon>
                      <Equalizer color="secondary" />
                    </ListItemIcon>
                    <ListItemText primary="Bestseller and genre comparisons" />
                  </ListItem>
                  <ListItem disableGutters={true}>
                    <ListItemIcon>
                      <Timer color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Full report available within minutes" />
                  </ListItem>
                  <ListItem disableGutters={true}>
                    <ListItemIcon>
                      <CreditCard color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Book Business Card to share in your pitches" />
                  </ListItem>
                </List>
              </Box>
              <Box flexGrow={0} flexShrink={0} mt={2}>
                <Button
                  component={RouterLink}
                  to="/assessment"
                  variant="text"
                  color="primary"
                >
                  Learn More
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

const Report = () => {
  return (
    <Box mt={8} mb={8}>
      <Grid container={true} spacing={4}>
        <Grid item={true} xs={12} md={6}>
          <Box mb={4}>
            <PageHeader
              align="left"
              title="Instant, affordable and objective manuscript evaluation"
              subtitle={
                <>
                  We believe that magic can happen when human creativity is
                  complemented by the power of today's cutting edge technology.{" "}
                  <Iris /> delivers actionable insights to assess and improve
                  fiction manuscripts.
                </>
              }
            />
          </Box>
          <Grid container={true} spacing={2}>
            <Grid item={true} xs={12} sm={4}>
              <Typography variant="h5" color="primary" gutterBottom={true}>
                5
              </Typography>
              <Typography color="textSecondary">
                5 predictive metrics powered by LLM technology: sales potential,
                reader rating, genre fit, ...
              </Typography>
            </Grid>
            <Grid item={true} xs={12} sm={4}>
              <Typography variant="h5" color="primary" gutterBottom={true}>
                30+
              </Typography>
              <Typography color="textSecondary">
                30+ NLP metrics to compare your manuscript against bestsellers
                and genre norms
              </Typography>
            </Grid>
            <Grid item={true} xs={12} sm={4}>
              <Typography variant="h5" color="primary" gutterBottom={true}>
                80%
              </Typography>
              <Typography color="textSecondary">
                <Iris />' sales potential predictions are ~80% accurate within
                an order of magnitude
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid
          item={true}
          xs={12}
          md={6}
          style={{
            position: "relative",
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <iframe
            width="560"
            height="315"
            src="https://www.youtube.com/embed/eRCKpvLIgkU?autoplay=1&mute=1&loop=1"
            title="YouTube video player"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen={true}
          />
          <Typography
            variant="subtitle1"
            align="center"
            style={{ marginTop: 24 }}
          >
            Check out a{" "}
            <Link href={SAMPLE_REPORT_URL} underline="none">
              sample report
            </Link>
            .
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
};

/*
const Specials = () => {
  const theme = useTheme();

  return (
    <Box my={8}>
      <PageHeader
        overline="Special deals"
        title="Sign up now to benefit from our special deals"
      />
      <Grid container={true} spacing={4}>
        <Grid item={true} xs={12} md={6}>
          <Card
            elevation={4}
            style={{
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.common.white,
            }}
          >
            <CardContent>
              <Typography
                variant="h6"
                color="inherit"
                gutterBottom={true}
                style={{ fontWeight: 700 }}
              >
                Free Webinar
              </Typography>
              <Box mt={2}>
                <Typography variant="body1" color="inherit">
                  Sign up for our free LIVE webinar and learn how to use
                  <Iris /> to improve your manuscript to land a publisher
                  contract and become a bestseller. It's 100% free, no strings
                  attached.
                </Typography>
              </Box>
            </CardContent>
            <CardActions>
              <Button
                color="inherit"
                component={RouterLink}
                to="https://docs.google.com/forms/d/e/1FAIpQLSdqOmZKD0dQilm2Xeyxvb8h8cdN-ZQMgzlIdICD6P3H2epvCw/viewform"
              >
                Sign up now
              </Button>
            </CardActions>
          </Card>
        </Grid>
        <Grid item={true} xs={12} md={6}>
          <Card
            elevation={4}
            style={{
              backgroundColor: orange[700],
              color: theme.palette.common.white,
            }}
          >
            <CardContent>
              <Typography
                variant="h6"
                color="inherit"
                gutterBottom={true}
                style={{ fontWeight: 700 }}
              >
                Want to get published?
              </Typography>
              <Box mt={2}>
                <Typography variant="body1" color="inherit">
                  If your manuscript scores higher than 80, we'll forward your
                  report to a set of publishers in your genre. With 100+
                  publishers at our fingertips, we're confident to pique
                  somebody's interest.
                </Typography>
              </Box>
            </CardContent>
            <CardActions>
              <Button
                color="inherit"
                component={HashLink}
                to="/authors#get_published"
              >
                Find Out More
              </Button>
            </CardActions>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
*/

/*
const SpecialsAndNews = () => {
  const theme = useTheme();
  const history = useHistory();

  return (
    <Box my={8}>
      <Grid container={true} spacing={4}>
        <Grid
          item={true}
          xs={12}
          md={7}
          style={{ display: "flex", flexDirection: "column" }}
        >
          <PageHeader overline="Special deals" />
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
            flexGrow={1}
          >
            <Box mb={2}>
              <Card
                elevation={4}
                style={{
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.common.white,
                }}
              >
                <CardContent>
                  <Typography
                    variant="h6"
                    color="inherit"
                    gutterBottom={true}
                    style={{ fontWeight: 700 }}
                  >
                    Free Webinar
                  </Typography>
                  <Box mt={2}>
                    <Typography variant="body1" color="inherit">
                      Sign up for our free LIVE webinar and learn how to use
                      <Iris /> to improve your manuscript to land a publisher
                      contract and become a bestseller. It's 100% free, no
                      strings attached.
                      <br />
                      <br />
                      You'll learn how to interpret <Iris />' reports, find your
                      manuscript's weak spots, see how you compare against your
                      genre, and to improve your writing.
                    </Typography>
                  </Box>
                </CardContent>
                <CardActions>
                  <Button
                    color="inherit"
                    component={RouterLink}
                    to="/free-webinar"
                  >
                    Sign up now
                  </Button>
                </CardActions>
              </Card>
            </Box>
            <Box mt={2}>
              <Card
                elevation={4}
                style={{
                  backgroundColor: orange[700],
                  color: theme.palette.common.white,
                }}
              >
                <CardContent>
                  <Typography
                    variant="h6"
                    color="inherit"
                    gutterBottom={true}
                    style={{ fontWeight: 700 }}
                  >
                    Want to get published?
                  </Typography>
                  <Box mt={2}>
                    <Typography variant="body1" color="inherit">
                      If your manuscript scores higher than 80, we'll forward
                      your report to a set of publishers in your genre. With
                      100+ publishers at our fingertips, we're confident to
                      pique somebody's interest.
                    </Typography>
                  </Box>
                </CardContent>
                <CardActions>
                  <Button
                    color="inherit"
                    component={HashLink}
                    to="/authors#get_published"
                  >
                    Find Out More
                  </Button>
                </CardActions>
              </Card>
            </Box>
          </Box>
        </Grid>
        <Grid item={true} xs={12} md={5}>
          <PageHeader overline="Latest News" />
          <BlogListEntry
            blog={COMPETITION_BLOG}
            onClick={() => history.push(`/blog/${COMPETITION_BLOG.slug}`)}
          />
          <Button
            variant="contained"
            size="large"
            color="primary"
            fullWidth={true}
            component={RouterLink}
            to="/blog"
            style={{ marginTop: 16 }}
          >
            View Blog
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};
*/

const SpecialsAndNews = () => {
  const history = useHistory();
  const theme = useTheme();

  return (
    <Box my={8}>
      <PageHeader
        overline={
          <>
            Latest News (
            <Link color="primary" component={RouterLink} to="/blog">
              Blog
            </Link>
            )
          </>
        }
      />
      <Grid container={true} spacing={4}>
        <Grid item={true} xs={12} md={4}>
          <BlogListEntry
            blog={EDITOR_LAUNCH_BLOG}
            onClick={() => history.push(`/blog/${EDITOR_LAUNCH_BLOG.slug}`)}
          />
        </Grid>
        <Grid item={true} xs={12} md={4}>
          <BlogListEntry
            blog={COMPETITION_WINNER_BLOG}
            onClick={() =>
              history.push(`/blog/${COMPETITION_WINNER_BLOG.slug}`)
            }
          />
        </Grid>
        <Grid item={true} xs={12} md={4}>
          <Card
            elevation={4}
            style={{
              backgroundColor: orange[700],
              color: theme.palette.common.white,
            }}
          >
            <CardContent>
              <Typography
                variant="h6"
                color="inherit"
                gutterBottom={true}
                style={{ fontWeight: 700 }}
              >
                Want to get published?
              </Typography>
              <Box mt={2}>
                <Typography variant="body1" color="inherit">
                  If your manuscript scores higher than 80, we'll forward your
                  report to a set of publishers in your genre. With 100+
                  publishers at our fingertips, we're confident to pique
                  somebody's interest.
                </Typography>
              </Box>
            </CardContent>
            <CardActions>
              <Button
                color="inherit"
                component={HashLink}
                to="/authors#get_published"
              >
                Find Out More
              </Button>
            </CardActions>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export const Benefits = () => {
  return (
    <Box my={8}>
      <PageHeader
        overline="Industry solutions"
        title="Powering the industry revolution"
      />
      <Grid container={true} spacing={8}>
        <Grid item={true} xs={12} sm={4}>
          <Box textAlign="center" mb={4}>
            <BenefitImage src={author} alt="Iris for authors" loading="lazy" />
          </Box>
          <Box mt={2}>
            <Typography
              variant="h6"
              gutterBottom={true}
              color="textPrimary"
              style={{ fontWeight: 700 }}
            >
              For authors
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Fiction is about both; the story and telling it right.
              <br />
              <br />
              Knowing what your audience enjoys in your genre and related
              sub-genres is critical, and may vary from region to region.
              <br />
              <br />
              Using our detailed metrics to compare each work with multiple
              benchmarks from the market, zeroing-in to find the underperforming
              sections in each manuscript can make all the difference.
            </Typography>
          </Box>
          <Box mt={2}>
            <Button color="primary" component={RouterLink} to="/authors">
              Iris for authors
            </Button>
          </Box>
        </Grid>
        <Grid item={true} xs={12} sm={4}>
          <Box textAlign="center" mb={4}>
            <BenefitImageSmaller
              src={publisher}
              alt="Iris for publishers"
              loading="lazy"
            />
          </Box>
          <Box mt={2}>
            <Typography
              variant="h6"
              gutterBottom={true}
              color="textPrimary"
              style={{ fontWeight: 700 }}
            >
              For publishers
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Finding the next best seller is like finding a needle in a
              haystack... but what if we give you a metal detector?
              <br />
              <br />
              Optimise your slush pile by using Quantifiction's proprietary{" "}
              <Iris /> technology to assess, filter and rank your incoming
              manuscripts!
              <br />
              <br />
              Free up your content acquisition team to focus on the highest
              potential scripts only.
            </Typography>
          </Box>
          <Box mt={2}>
            <Button color="primary" component={RouterLink} to="/publishers">
              Iris for publishers
            </Button>
          </Box>
        </Grid>
        <Grid item={true} xs={12} sm={4}>
          <Box textAlign="center" mb={4}>
            <BenefitImage src={agent} alt="Iris for agents" loading="lazy" />
          </Box>
          <Box mt={2}>
            <Typography
              variant="h6"
              gutterBottom={true}
              color="textPrimary"
              style={{ fontWeight: 700 }}
            >
              For agents
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Collaborate closely with authors to continuously improve their
              manuscripts using our <Iris /> technology.
              <br />
              <br />
              Integrate our insights into publisher pitches and leverage our
              automated editorial improvement reports to help your authors
              refine their manuscripts.
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

const GetStarted = () => {
  return (
    <Box my={4} py={8} bgcolor={darken(lightBlue[900], 0.3)}>
      <PageHeader
        overline="Get Started"
        title={
          <>
            Get started with <Iris /> today
          </>
        }
        align="center"
        darkMode={true}
      />
      <Box display="flex" justifyContent="center" color="white">
        <SignupButton place="home-page" />
        <Button
          variant="outlined"
          color="inherit"
          href={"mailto:" + INFO_EMAIL}
          style={{ marginLeft: 16 }}
        >
          Contact us
        </Button>
      </Box>
    </Box>
  );
};

export default MarketingPage;



// WEBPACK FOOTER //
// ./src/pages/MarketingPage.tsx